import React, { useState, useEffect } from 'react';
import { Plus, Loader2, AlertCircle, FileSpreadsheet } from 'lucide-react';
import * as dpService from 'features/dp/services/dpService';
import ResumoFolha from './ResumoFolha';
import ValesTab from './ValesTab';
import HoleriteModal from './HoleriteModal';
import {
  FOLHA_STATUS_MAP,
  FOLHA_TIPO_LABELS,
  fmtBRL,
  FolhaStat,
} from './RubricaSection';

/**
 * FolhaTab – main Folha de Pagamento tab with 3 sub-tabs:
 *   Folhas | Resumo | Vales
 *
 * Props:
 *   podeEditar – boolean, controls whether create/calculate/close actions are visible
 */
export default function FolhaTab({ podeEditar = false }) {
  const [folhas, setFolhas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [mostrarFormCriar, setMostrarFormCriar] = useState(false);
  const [acao, setAcao] = useState(null); // { folhaId, tipo }
  const [holeriteAberto, setHoleriteAberto] = useState(null); // folhaId to open modal
  const [subAba, setSubAba] = useState('lista');

  // ── Fetch folhas ──
  const carregarFolhas = async () => {
    try {
      setLoading(true);
      setErro(null);
      const resp = await dpService.listarFolhas({ por_pagina: 30 });
      const raw = resp.data?.data;
      setFolhas(raw?.data ?? raw ?? []);
    } catch {
      setErro('Não foi possível carregar as folhas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarFolhas();
  }, []);

  // ── Calculate folha ──
  const calcularFolha = async (folhaId) => {
    try {
      setAcao({ folhaId, tipo: 'calcular' });
      await dpService.calcularFolha(folhaId);
      await carregarFolhas();
    } catch (err) {
      window.alert(err.response?.data?.message ?? 'Erro ao calcular folha.');
    } finally {
      setAcao(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* ── Header with sub-tab switcher ── */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-[color:var(--text)]">
            Folha de Pagamento
          </h3>
          <p className="text-xs text-[color:var(--text-muted)] mt-0.5">
            Cálculo automático de INSS, IRRF e FGTS
          </p>
        </div>

        {/* Sub-tab buttons */}
        <div className="flex rounded-lg border border-[color:var(--border)] overflow-hidden mr-2">
          <button
            onClick={() => setSubAba('lista')}
            className={
              'px-3 py-1.5 text-xs font-medium transition-colors ' +
              (subAba === 'lista'
                ? 'bg-teal-600 text-white'
                : 'bg-[color:var(--surface)] text-[color:var(--text-muted)] hover:bg-[color:var(--surface-muted)]')
            }
          >
            📋 Folhas
          </button>
          <button
            onClick={() => setSubAba('resumo')}
            className={
              'px-3 py-1.5 text-xs font-medium transition-colors ' +
              (subAba === 'resumo'
                ? 'bg-teal-600 text-white'
                : 'bg-[color:var(--surface)] text-[color:var(--text-muted)] hover:bg-[color:var(--surface-muted)]')
            }
          >
            📊 Resumo
          </button>
          <button
            onClick={() => setSubAba('vales')}
            className={
              'px-3 py-1.5 text-xs font-medium transition-colors ' +
              (subAba === 'vales'
                ? 'bg-teal-600 text-white'
                : 'bg-[color:var(--surface)] text-[color:var(--text-muted)] hover:bg-[color:var(--surface-muted)]')
            }
          >
            💵 Vales
          </button>
        </div>

        {/* "Abrir Folha" button (only if podeEditar) */}
        {podeEditar && (
          <button
            onClick={() => setMostrarFormCriar(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-teal-600 text-white hover:bg-teal-700 transition-colors"
          >
            <Plus size={15} />
            Abrir Folha
          </button>
        )}
      </div>

      {/* ── Sub-tab content ── */}
      {subAba === 'resumo' ? (
        <ResumoFolha folhas={folhas} />
      ) : subAba === 'vales' ? (
        <ValesTab />
      ) : (
        <>
          {/* Create form placeholder (injected externally as uq) */}
          {mostrarFormCriar && (
            <CriarFolhaPlaceholder
              onSalvo={() => {
                setMostrarFormCriar(false);
                carregarFolhas();
              }}
              onCancelar={() => setMostrarFormCriar(false)}
            />
          )}

          {/* Loading spinner */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 size={32} className="animate-spin text-teal-600" />
            </div>
          ) : erro ? (
            /* Error message */
            <div className="flex items-center gap-2 text-red-500 bg-red-50 dark:bg-red-900/20 p-4 rounded-xl">
              <AlertCircle size={16} />
              <span className="text-sm">{erro}</span>
            </div>
          ) : folhas.length === 0 ? (
            /* Empty state */
            <div className="text-center py-16 text-[color:var(--text-muted)]">
              <FileSpreadsheet size={40} className="mx-auto mb-3 opacity-40" />
              <p className="font-medium">Nenhuma folha encontrada</p>
              {podeEditar && (
                <p className="text-sm mt-1">
                  Clique em "Abrir Folha" para criar a primeira folha.
                </p>
              )}
            </div>
          ) : (
            /* Folha cards */
            <div className="space-y-3">
              {folhas.map((folha) => {
                const status = FOLHA_STATUS_MAP[folha.status] || FOLHA_STATUS_MAP.aberta;
                const isAcaoAtiva = acao?.folhaId === folha.id;

                return (
                  <div
                    key={folha.id}
                    className="border border-[color:var(--border)] rounded-2xl p-4 hover:bg-[color:var(--surface-muted)] transition-colors"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      {/* Left info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-[color:var(--text)]">
                            {folha.competencia
                              ? folha.competencia.split('-').reverse().join('/')
                              : '—'}
                          </span>
                          <span className="text-xs text-[color:var(--text-muted)]">
                            {FOLHA_TIPO_LABELS[folha.tipo] ?? folha.tipo}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.className}`}
                          >
                            {status.label}
                          </span>
                        </div>

                        {/* Totals (only for calculada or fechada) */}
                        {(folha.status === 'calculada' || folha.status === 'fechada') && (
                          <div className="flex flex-wrap gap-4 mt-2">
                            <FolhaStat
                              label="Proventos"
                              value={fmtBRL(folha.total_proventos)}
                              cor="text-green-600"
                            />
                            <FolhaStat
                              label="Descontos"
                              value={fmtBRL(folha.total_descontos)}
                              cor="text-red-500"
                            />
                            <FolhaStat
                              label="Líquido"
                              value={fmtBRL(folha.total_liquido)}
                              cor="text-teal-600"
                              bold
                            />
                            <FolhaStat
                              label="FGTS"
                              value={fmtBRL(folha.total_fgts)}
                              cor="text-blue-600"
                            />
                          </div>
                        )}
                      </div>

                      {/* Right actions */}
                      {podeEditar && (
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {folha.status === 'aberta' && (
                            <button
                              onClick={() => calcularFolha(folha.id)}
                              disabled={isAcaoAtiva}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-xl bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 transition-colors"
                            >
                              {isAcaoAtiva && (
                                <Loader2 size={12} className="animate-spin" />
                              )}
                              Calcular
                            </button>
                          )}

                          {(folha.status === 'calculada' || folha.status === 'fechada') && (
                            <button
                              onClick={() => setHoleriteAberto(folha.id)}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-xl border border-[color:var(--border)] text-[color:var(--text)] hover:bg-[color:var(--surface-muted)] transition-colors"
                            >
                              Ver Holerites
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Holerite modal ── */}
          {holeriteAberto && (
            <HoleriteModal
              folhaId={holeriteAberto}
              onFechar={() => setHoleriteAberto(null)}
            />
          )}
        </>
      )}
    </div>
  );
}

/**
 * Placeholder component for the "Criar Folha" form.
 * In the original bundle this was the `uq` component which is injected externally.
 * Replace this with the actual CriarFolha component when available.
 */
function CriarFolhaPlaceholder({ onSalvo, onCancelar }) {
  const [competencia, setCompetencia] = useState('');
  const [tipo, setTipo] = useState('mensal');
  const [salvando, setSalvando] = useState(false);

  const handleSalvar = async () => {
    if (!competencia) return;
    setSalvando(true);
    try {
      await dpService.criarFolha({ competencia, tipo });
      onSalvo();
    } catch (err) {
      window.alert(err.response?.data?.message ?? 'Erro ao criar folha.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="border border-[color:var(--border)] rounded-2xl p-5 space-y-4 bg-[color:var(--surface-muted)]">
      <h4 className="font-semibold text-sm text-[color:var(--text)]">Abrir Nova Folha</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">
            Competência
          </label>
          <input
            type="month"
            value={competencia}
            onChange={(e) => setCompetencia(e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text)]"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">
            Tipo
          </label>
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text)]"
          >
            {Object.entries(FOLHA_TIPO_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <button
          onClick={onCancelar}
          className="px-3 py-1.5 text-sm rounded-xl border border-[color:var(--border)] hover:bg-[color:var(--surface)]"
        >
          Cancelar
        </button>
        <button
          onClick={handleSalvar}
          disabled={salvando || !competencia}
          className="px-4 py-1.5 text-sm font-medium rounded-xl bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
        >
          {salvando ? 'Salvando...' : 'Criar Folha'}
        </button>
      </div>
    </div>
  );
}
